// src/components/RecordMetadataPills.tsx
import React from 'react';
import { Box, Chip } from '@mui/material';

const severityColors: Record<string, string> = {
    Unimportant: '#a2d5a2',
    Low: '#7ac97a',
    Medium: '#fdd26e',
    High: '#f59c6b',
    Critical: '#f06b6b',
    'No Severity': '#ccc',
};

interface Props {
    severity: string | null;
    tags: string[];
    isMobile: boolean;
}

// max total characters across all tag labels (for desktop)
const MAX_TAG_CHARS = 50;

const RecordMetadataPills: React.FC<Props> = ({ severity, tags, isMobile }) => {
    if (isMobile) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    overflowX: 'auto',
                    gap: 1,
                    py: 0.5,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                {severity && (
                    <Chip
                        size="small"
                        label={severity}
                        sx={{
                            fontWeight: 500,
                            backgroundColor: severityColors[severity] ?? '#ccc',
                            color: '#000',
                            height: 24,
                            flexShrink: 0,
                        }}
                    />
                )}
                {tags.map((tag) => (
                    <Chip
                        key={tag}
                        size="small"
                        label={tag}
                        sx={{
                            backgroundColor: '#e0e0e0',
                            color: '#333',
                            height: 24,
                            flexShrink: 0,
                        }}
                    />
                ))}
            </Box>
        );
    }

    const displayedTags: string[] = [];
    let charCount = 0;
    for (const tag of tags) {
        if (charCount + tag.length > MAX_TAG_CHARS) break;
        displayedTags.push(tag);
        charCount += tag.length;
    }
    const overflowCount = tags.length - displayedTags.length;

    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
            {severity && (
                <Chip
                    size="small"
                    label={severity}
                    sx={{
                        fontWeight: 500,
                        backgroundColor: severityColors[severity] ?? '#ccc',
                        color: '#000',
                        height: 24,
                    }}
                />
            )}
            {displayedTags.map((tag) => (
                <Chip
                    key={tag}
                    size="small"
                    label={tag}
                    sx={{
                        backgroundColor: '#e0e0e0',
                        color: '#333',
                        height: 24,
                    }}
                />
            ))}
            {overflowCount > 0 && (
                <Chip
                    size="small"
                    label={`+${overflowCount} more`}
                    sx={{
                        backgroundColor: '#e0e0e0',
                        color: '#333',
                        height: 24,
                    }}
                />
            )}
        </Box>
    );
};

export default RecordMetadataPills;
